import {
  buildComparisonRows,
  getKnownEnvelopes,
} from '@src/utility/distributionChannelComparison';
import { Fragment, useMemo } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import ChannelIcon from './ChannelIcon';
import {
  CHANNEL_TOOLTIP_STYLE,
  renderChannelFormatTooltip,
} from './channelTooltipContent';

import type { DistributionChannel } from '@src/types.ts/distribution-channel';

interface Props {
  channels: DistributionChannel[];
  variant?: 'summary' | 'details';
}

const ChannelComparisonTable = ({ channels, variant = 'details' }: Props) => {
  const rows = useMemo(
    () => (variant === 'details' ? buildComparisonRows(channels) : []),
    [channels, variant],
  );
  const envelopes = useMemo(
    () => (variant === 'summary' ? getKnownEnvelopes() : []),
    [variant],
  );

  if (channels.length === 0) {
    return <Empty>No comparable formats across the selected channels.</Empty>;
  }
  if (variant === 'details' && rows.length === 0) {
    return <Empty>No comparable formats across the selected channels.</Empty>;
  }

  return (
    <Scroller>
      <Table $variant={variant}>
        <thead>
          <tr>
            <StickyTh $col="format">Channel</StickyTh>
            <StickyTh $col="aspect" />
            {channels.map(channel => (
              <th key={channel.id} title={channel.title}>
                <HeaderCell>
                  <HeaderChip>
                    <ChannelIcon
                      iconUrl={channel.iconUrl}
                      alt={`${channel.title} icon`}
                      size={20}
                      variant="plain"
                    />
                  </HeaderChip>
                  <HeaderTitle>{channel.title}</HeaderTitle>
                </HeaderCell>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {variant === 'summary'
            ? envelopes.map(envelope => (
                <tr key={envelope.formatType} data-new-format>
                  <FormatCell>{envelope.label}</FormatCell>
                  <AspectCell>Supported</AspectCell>
                  {channels.map(channel => {
                    const format = channel.formats.find(
                      f => f.formatType === envelope.formatType,
                    );
                    const supports = !!format;
                    return (
                      <SupportCell key={channel.id} $supports={supports}>
                        <span
                          data-tooltip-id={`cmp-${envelope.formatType}-${channel.id}`}
                        >
                          {supports ? '✓' : '✗'}
                        </span>
                        <Tooltip
                          id={`cmp-${envelope.formatType}-${channel.id}`}
                          noArrow
                          render={() =>
                            renderChannelFormatTooltip({
                              channel,
                              format,
                            })
                          }
                          style={CHANNEL_TOOLTIP_STYLE}
                        />
                      </SupportCell>
                    );
                  })}
                </tr>
              ))
            : rows.map((row, idx) => {
                const previous = rows[idx - 1];
                const isNewFormat =
                  !previous || previous.formatType !== row.formatType;
                return (
                  <Fragment key={`${row.formatType}-${row.aspect}`}>
                    {isNewFormat && (
                      <tr data-new-format>
                        <FormatCell>{row.formatLabel}</FormatCell>
                        <AspectCell>Supported</AspectCell>
                        {channels.map(channel => {
                          const supports = channel.formats.some(
                            f => f.formatType === row.formatType,
                          );
                          return (
                            <SupportCell key={channel.id} $supports={supports}>
                              {supports ? '✓' : '✗'}
                            </SupportCell>
                          );
                        })}
                      </tr>
                    )}
                    <tr>
                      <FormatCell />
                      <AspectCell>{row.aspectLabel}</AspectCell>
                      {channels.map(channel => {
                        const value = row.cellsByChannelId[channel.id];
                        return (
                          <ValueCell key={channel.id} $missing={!value}>
                            {value ?? '—'}
                          </ValueCell>
                        );
                      })}
                    </tr>
                  </Fragment>
                );
              })}
        </tbody>
      </Table>
    </Scroller>
  );
};

export default ChannelComparisonTable;

const Empty = styled.div`
  font-size: var(--text-sm);
  color: var(--text-secondary);
  padding: 24px 0;
`;

const Scroller = styled.div`
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--background);
`;

const FORMAT_COL_WIDTH = 160;
const ASPECT_COL_WIDTH = 120;
const CHANNEL_COL_WIDTH_DETAILS = 180;

const Table = styled.table<{ $variant: 'summary' | 'details' }>`
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
  font-size: var(--text-sm);
  /* Summary fits any reasonable width — fixed layout distributes channel
     columns evenly with no overflow. Details legitimately overflows on most
     viewports, so let it size to content and scroll. */
  table-layout: ${p => (p.$variant === 'summary' ? 'fixed' : 'auto')};

  th,
  td {
    padding: 12px 16px;
    text-align: left;
    vertical-align: middle;
    border-bottom: 1px solid var(--border-subtle, var(--border));
  }

  thead th {
    padding: 14px 16px;
    background: var(--surface-elevated);
    border-bottom: 1px solid var(--border);
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr[data-new-format] td {
    border-top: 1px solid var(--border);
    padding-top: 16px;
  }

  /* Translucent tint on data cells only — the sticky FormatCell / AspectCell
     paint the tint themselves on top of a solid var(--background) so they
     fully occlude horizontally-scrolled content. */
  tbody tr[data-new-format] td:nth-child(n + 3) {
    background: var(--badge-blue-bg);
  }

  tbody tr:not([data-new-format]) td {
    padding-top: 6px;
    padding-bottom: 6px;
  }

  tbody tr:hover td {
    background: var(--surface-elevated);
  }

  thead th:nth-child(n + 3),
  tbody td:nth-child(n + 3) {
    text-align: center;
    ${p =>
      p.$variant === 'details'
        ? `min-width: ${CHANNEL_COL_WIDTH_DETAILS}px;`
        : ''}
  }
`;

const HeaderCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const HeaderChip = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 1px;
  border-radius: 6px;
  box-sizing: border-box;
  background: var(--text-muted);
  border: 1px solid var(--text-muted);

  img {
    filter: brightness(0);
  }
`;

const HeaderTitle = styled.span`
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
  white-space: nowrap;
`;

// Tinted solid: var(--background) (#0e0e10) composited with badge-blue at 6%.
// Precomputed to avoid layered backgrounds, which were getting overridden by
// the table-level cascade and leaving the sticky cells transparent.
const NEW_FORMAT_STICKY_BG = '#11151e';

const FormatCell = styled.td`
  && {
    position: sticky;
    left: 0;
    z-index: 3;
    background: var(--background);
    width: ${FORMAT_COL_WIDTH}px;
    min-width: ${FORMAT_COL_WIDTH}px;
    font-weight: var(--font-weight-bold);
    color: var(--foreground);
    text-transform: uppercase;
    font-size: 11px;
    letter-spacing: 0.04em;
    white-space: nowrap;
  }

  tr:hover && {
    background: var(--background);
  }

  tr[data-new-format] && {
    background: ${NEW_FORMAT_STICKY_BG};
  }
`;

const AspectCell = styled.td`
  && {
    position: sticky;
    left: ${FORMAT_COL_WIDTH}px;
    z-index: 3;
    background: var(--background);
    width: ${ASPECT_COL_WIDTH}px;
    min-width: ${ASPECT_COL_WIDTH}px;
    color: var(--text-secondary);
    white-space: nowrap;
    box-shadow: 1px 0 0 0 var(--border);
  }

  tr:hover && {
    background: var(--surface-elevated);
  }

  tr[data-new-format] && {
    background: ${NEW_FORMAT_STICKY_BG};
  }
`;

const StickyTh = styled.th<{ $col: 'format' | 'aspect' }>`
  position: sticky;
  left: ${p => (p.$col === 'format' ? 0 : `${FORMAT_COL_WIDTH}px`)};
  z-index: 3;
  background: ${NEW_FORMAT_STICKY_BG} !important;
  width: ${p =>
    p.$col === 'format' ? `${FORMAT_COL_WIDTH}px` : `${ASPECT_COL_WIDTH}px`};
  min-width: ${p =>
    p.$col === 'format' ? `${FORMAT_COL_WIDTH}px` : `${ASPECT_COL_WIDTH}px`};
  ${p => p.$col === 'aspect' && 'box-shadow: 1px 0 0 0 var(--border);'}
`;

const ValueCell = styled.td<{ $missing: boolean }>`
  color: ${p => (p.$missing ? 'var(--text-muted)' : 'var(--foreground)')};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const SupportCell = styled.td<{ $supports: boolean }>`
  --rt-opacity: 1;
  color: ${p => (p.$supports ? 'var(--green-500)' : 'var(--destructive)')};
  font-size: 18px;
  font-weight: var(--font-weight-bold);
  text-align: center;
`;
